import React, { useMemo, useState } from "react";
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { compareAsc, parseISO } from "date-fns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toDefaultDateTimeFormat } from "@/helpers/dates";
import { FileItemStatus, FileItem } from "@/views/home/upload/uploadMocks";

interface FileTableProps {
  files: FileItem[];
  limit?: number;
}

export const DashboardFileTable: React.FC<FileTableProps> = ({ files, limit = 5 }) => {
  const [filter] = useState<string>("");

  const columns: ColumnDef<FileItem>[] = useMemo(() => {
    const renderStatusBadge = (status: FileItemStatus) => {
      const statusStyles: Record<FileItemStatus, string> = {
        uploaded: "bg-indigo-700/10 text-indigo-400 ring-indigo-500/20",
        "processed by tracker": "bg-blue-400/10 text-blue-400 ring-blue-400/30",
        uploading: "bg-yellow-400/10 text-violet-500 ring-violet-400/20",
        verified: "bg-gray-400/10 text-gray-400 ring-gray-400/20",
        processing: "bg-yellow-400/10 text-violet-500 ring-violet-400/20",
        processed: "bg-gray-400/10 text-gray-400 ring-gray-400/20",
      };

      return (
        <span
          className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${statusStyles[status]}`}
        >
          {status}
        </span>
      );
    };

    return [
      {
        accessorKey: "uploadDate",
        header: "Upload Date",
        cell: info => toDefaultDateTimeFormat(info.getValue() as string),
      },
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: info => renderStatusBadge(info.getValue() as FileItemStatus),
      },
    ];
  }, []);

  const filteredFiles = useMemo(
    () =>
      files.filter(
        file =>
          file.name.toLowerCase().includes(filter.toLowerCase()) ||
          file.type.toLowerCase().includes(filter.toLowerCase()) ||
          file.status.toLowerCase().includes(filter.toLowerCase()),
      ),
    [files, filter],
  );

  const sortedFiles = useMemo(
    () => filteredFiles.sort((a, b) => compareAsc(parseISO(b.uploadDate), parseISO(a.uploadDate))),
    [filteredFiles],
  );

  const limitedFiles = useMemo(() => sortedFiles.slice(0, limit), [sortedFiles, limit]);

  const table = useReactTable({
    data: limitedFiles,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => (window.location.href = "/drive/")}
                  className="cursor-pointer"
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
